import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_MUSIC_SRC = 'assets/music/winning-the-lottery_by_commercialclips_preview.aac';
const VOLUME_STORAGE_KEY = 'background_music_volume';
const MUTE_STORAGE_KEY = 'background_music_muted_ios';

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function MusicPlayer({ src = DEFAULT_MUSIC_SRC }) {
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [showVolume, setShowVolume] = useState(false);
  const [volume, setVolume] = useState(() => {
    const stored = localStorage.getItem(VOLUME_STORAGE_KEY);
    return stored !== null ? parseFloat(stored) : 0.1;
  });
  const [muted, setMuted] = useState(() => {
    if (isIOS()) {
      const stored = localStorage.getItem(MUTE_STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  useEffect(() => {
    if (audioRef.current) {
      if (isIOS()) {
        audioRef.current.muted = muted;
      } else {
        audioRef.current.volume = volume;
      }
    }
    if (isIOS()) {
      localStorage.setItem(MUTE_STORAGE_KEY, muted.toString());
    } else {
      localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
    }
  }, [volume, muted]);

  useEffect(() => {
    const playOnInteraction = () => {
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(() => {});
      }
      window.removeEventListener('click', playOnInteraction);
      window.removeEventListener('keydown', playOnInteraction);
    };
    window.addEventListener('click', playOnInteraction);
    window.addEventListener('keydown', playOnInteraction);
    return () => {
      window.removeEventListener('click', playOnInteraction);
      window.removeEventListener('keydown', playOnInteraction);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (audioRef.current) {
        if (document.hidden) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!showVolume) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowVolume(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showVolume]);

  const getVolumeIcon = () => {
    if (isIOS() && muted) return '🔇';
    if (!isIOS() && volume === 0) return '🔇';
    if (!isIOS() && volume < 0.4) return '🔈';
    if (!isIOS() && volume < 0.7) return '🔉';
    return '🔊';
  };

  return (
    <div ref={containerRef} style={{ position: 'fixed', bottom: 5, left: 10, zIndex: 9999 }}>
      <audio
        ref={audioRef}
        src={src}
        loop
        autoPlay
        preload="auto"
      />
      <div style={{ position: 'relative', display: 'inline-block' }}>
        {isIOS() ? (
          <button
            onClick={() => setMuted((m) => !m)}
            style={{
              background: 'rgba(0,0,0,0.5)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 20,
              height: 20,
              cursor: 'pointer',
              fontSize: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label={muted ? 'Unmute background music' : 'Mute background music'}
            title={muted ? 'Unmute music' : 'Mute music'}
          >
            {getVolumeIcon()}
          </button>
        ) : (
          <>
            <button
              onClick={() => setShowVolume((v) => !v)}
              style={{
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 20,
                height: 20,
                cursor: 'pointer',
                fontSize: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Adjust background music volume"
              title="Adjust music volume"
            >
              {getVolumeIcon()}
            </button>
            {showVolume && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 30,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.8)',
                  padding: 8,
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 10000,
                }}
              >
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={e => setVolume(parseFloat(e.target.value))}
                  style={{
                    writingMode: 'bt-lr', // vertical
                    WebkitAppearance: 'slider-vertical',
                    height: 60,
                    width: 18,
                    margin: 0,
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MusicPlayer; 